'use client'

/**
 * FRIDGE-872 — one compared product, as the house product card.
 *
 * The anatomy is the homepage canvas `.pcard` verbatim (remove · heart · media
 * · brand · title link · identifier chip + model · price · retailer ladder ·
 * deal call to action), so a product on /compare looks exactly like the same
 * product on the home page. The two differences are deliberate and belong to
 * this page: a remove control, because a comparison is a set you edit, and the
 * retailer ladder under the price, because "which shop is cheapest today" is
 * the question this page exists to answer.
 *
 * The call-to-action label comes from lib/deal-cta — "Get Deal" when the card
 * shows a price, "See Deal" when it does not — bound to the SAME boolean that
 * gates the price element, never to a second field that merely correlates.
 * Pinned by tests/product-card-deal-cta.test.ts.
 */
import { ComparisonContextLink } from '@/components/product/comparison-context-link'
import { SafeImage } from '@/components/ui/safe-image'
import { NoImageFallback } from '@/components/ui/no-image-fallback'
import { Icon } from '@/components/homepage-2026/canvas/sprite'
import { RetailerMark } from '@/components/homepage-2026/retailer-mark'
import { formatPrice } from '@/components/homepage-2026/format'
import { dealCtaLabel } from '@/lib/deal-cta'
import { productCardCtaName, productCardLinkName } from '@/lib/card-link-names'
import type { CompareRow } from './compare-product'

/** How many retailer rows fit under a card without turning it into a table of its own. */
const LADDER_ROWS = 3

export function CompareCard({
  product,
  isBestPrice,
  saved,
  onRemove,
  onToggleSaved,
}: {
  product: CompareRow
  isBestPrice: boolean
  saved: boolean
  onRemove: () => void
  onToggleSaved: () => void
}) {
  const showsPrice = product.priceCents !== null && formatPrice(product.priceCents) !== ''
  const label = dealCtaLabel(showsPrice)
  const identifier = product.model ?? product.fin
  const rows = product.offers.slice(0, LADDER_ROWS)

  return (
    <div className="pcard" data-fin={product.fin ?? undefined}>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${product.title} from the comparison`}
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 2,
          width: 28,
          height: 28,
          borderRadius: 9999,
          border: '1px solid #e5e7eb',
          background: '#ffffff',
          color: '#6a7282',
          fontSize: 14,
          lineHeight: 1,
          fontWeight: 700,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✕
      </button>

      <button
        type="button"
        className="phe"
        onClick={onToggleSaved}
        aria-pressed={saved}
        aria-label={saved ? `Remove ${product.title} from saved` : `Save ${product.title}`}
        style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: saved ? '#dc2626' : undefined }}
      >
        <Icon id="i-heart" className="i" />
      </button>

      <ComparisonContextLink
        href={product.href}
        fin={product.fin}
        className="pmedia"
        aria-label={productCardLinkName(product.title, product.href, ' | Fridge.com', identifier)}
      >
        {product.image ? (
          <SafeImage
            src={product.image}
            alt={`${product.brand} ${product.title}`.trim()}
            width={220}
            height={220}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <span style={{ display: 'block', width: 120, height: 120, borderRadius: 8, overflow: 'hidden' }}>
            <NoImageFallback />
          </span>
        )}
      </ComparisonContextLink>

      {product.brand ? <span className="pbrand">{product.brand}</span> : null}

      <span className="ptitle">
        <ComparisonContextLink
          className="ptlink"
          href={product.href}
          fin={product.fin}
          aria-label={productCardLinkName(product.title, product.href, '', identifier)}
        >
          {product.title}
        </ComparisonContextLink>
      </span>

      <span className="pmodel">
        {product.fin ? <span className="idchip">{product.fin}</span> : null}
        {/* FRIDGE-1031: its own flex item so it can ellipsize; the full model stays DOM text and rides the tooltip. */}
        {product.model ? <span className="pmtext" title={`Model ${product.model}`}>{`${product.fin ? ' · ' : ''}Model ${product.model}`}</span> : null}
      </span>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        {showsPrice ? <span className="pprice">{formatPrice(product.priceCents as number)}</span> : null}
        {showsPrice && isBestPrice ? (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#101828',
              background: '#ff9951',
              borderRadius: 9999,
              padding: '2px 9px',
            }}
          >
            Lowest price
          </span>
        ) : null}
      </div>

      {rows.length ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {rows.map((offer, i) => (
            <div
              key={offer.retailer}
              className="retrow"
              style={
                i === 0
                  ? { background: '#fff4ea', borderLeft: '3px solid #ff9951', paddingLeft: 8, marginLeft: -8, borderRadius: 4, borderBottom: 'none' }
                  : i === rows.length - 1
                    ? { borderBottom: 'none' }
                    : undefined
              }
            >
              <span className="retname" style={i === 0 ? { fontWeight: 700 } : undefined}>
                <RetailerMark name={offer.retailer} />
                {offer.retailer}
              </span>
              <span style={{ fontWeight: i === 0 ? 800 : 600 }}>{formatPrice(offer.priceCents)}</span>
            </div>
          ))}
          {product.offers.length > rows.length ? (
            <span style={{ fontSize: 11.5, color: '#6a7282', paddingTop: 6 }}>
              +{product.offers.length - rows.length} more retailer{product.offers.length - rows.length === 1 ? '' : 's'} on the product page
            </span>
          ) : null}
        </div>
      ) : (
        <span style={{ fontSize: 11.5, color: '#6a7282' }}>Retailer prices open on the product page.</span>
      )}

      <ComparisonContextLink
        className="pcta"
        href={product.href}
        fin={product.fin}
        aria-label={productCardCtaName(label, { brand: product.brand, title: product.title, identifier }, product.href)}
      >
        {label}
      </ComparisonContextLink>
    </div>
  )
}
